import Axios from 'api/client';
import { DEFAULT_MULTI_SEARCH_LIMIT } from 'components/search/constants';
import { ContactType } from 'constants/contacts';
import { SearchCollectionName, SearchCollections } from 'types/domain';

const MULTI_SEARCH_PAGE = 1;
const HELP_CENTER_LIMIT = 1;
const CONTACTS_QUERY_BY_KEYS =
    'first_name,middle_name,last_name,primary_email,primary_phone,company,note,company_name';

interface SearchResponse {
    results: SearchResult[];
}

interface SearchResult<T = unknown> {
    facet_counts: any[];
    found: number;
    hits: SearchHit<T>[];
    out_of: number;
    page: number;
    request_params: SearchRequestParams;
    search_cutoff: boolean;
    search_time_ms: number;
    code?: number;
    error?: string;
}

interface SearchHit<T = unknown> {
    document: SearchDocument<T>;
    highlight: SearchHighlight<T>;
    highlights: SearchHighlightWithField[];
    text_match: number;
    text_match_info: SearchTextMatchInfo;
}

type SearchDocument<T = unknown> = T;

type SearchHighlight<T = unknown> = {
    [key in keyof T]: SearchHighlightDetail | undefined;
};

interface SearchHighlightWithField extends SearchHighlightDetail {
    field: string;
}

interface SearchHighlightDetail {
    matched_tokens: string[];
    snippet: string;
}

interface SearchTextMatchInfo {
    best_field_score: string;
    best_field_weight: number;
    fields_matched: number;
    score: string;
    tokens_matched: number;
}

interface SearchRequestParams {
    collection_name: SearchCollectionName;
    per_page: number;
    q: string;
}

interface SearchParam {
    limit: number;
    page: number;
    collection: SearchCollectionName;
    originalCollection: SearchCollectionName;
    q: string;
    query_by: string;
    filter_by?: string;
}

const search =
    (
        apiUrl: string,
        apiKey: string,
        accountResources: string[] = [],
        prefix: SearchCollections = {} as SearchCollections,
        limit: number = DEFAULT_MULTI_SEARCH_LIMIT,
    ) =>
    (query: string = '', resources: string[] = []) =>
        Axios.post<SearchResponse>(
            `${apiUrl}/multi_search`,
            {
                searches: getSearchParams(query, prefix, limit)
                    .filter(q => filterByResources(q.originalCollection, accountResources))
                    .filter(q => filterByResources(q.originalCollection, resources))
                    .map(({ originalCollection: _, ...q }) => q),
            },
            {
                params: {
                    'x-typesense-api-key': apiKey,
                },
                withCredentials: false,
            },
        ).then(response => response.data.results);

const getSearchParams = (query: string, prefix: SearchCollections, limit: number) => {
    const curriedPrependPrefix = (
        resource: SearchCollectionName,
    ): [SearchCollectionName, SearchCollectionName] => {
        const possiblePrefix = prefix[resource];
        const nextPrefix =
            possiblePrefix && resource.length !== possiblePrefix.length
                ? (possiblePrefix as SearchCollectionName)
                : resource;

        return [nextPrefix, resource];
    };

    const curriedSearchParamsResourceFactory = (
        prefix: [
            Parameters<typeof searchParamsResourceFactory>[1],
            Parameters<typeof searchParamsResourceFactory>[2],
        ],
        queryBy: Parameters<typeof searchParamsResourceFactory>[3],
        filterBy?: string,
    ) => searchParamsResourceFactory(query, ...prefix, queryBy, limit, filterBy);

    return [
        curriedSearchParamsResourceFactory(
            curriedPrependPrefix('contacts'),
            CONTACTS_QUERY_BY_KEYS,
            `type:=${ContactType.Individual}`,
        ),
        curriedSearchParamsResourceFactory(
            curriedPrependPrefix('contacts'),
            CONTACTS_QUERY_BY_KEYS,
            `type:=${ContactType.Company}`,
        ),
        curriedSearchParamsResourceFactory(curriedPrependPrefix('campaigns'), 'title'),
        curriedSearchParamsResourceFactory(
            curriedPrependPrefix('tickets'),
            'uid,uid_suffix,contact.first_name,contact.last_name,contact.primary_email,contact.primary_phone',
        ),
        curriedSearchParamsResourceFactory(
            curriedPrependPrefix('transactions'),
            'number,contact.first_name,contact.last_name,contact.primary_email,contact.primary_phone,contact.company_name',
        ),
        curriedSearchParamsResourceFactory(
            curriedPrependPrefix('transaction_plans'),
            'contact.first_name,contact.last_name,contact.primary_email,contact.primary_phone',
        ),
        searchParamsResourceFactory(
            query,
            'help_center',
            'help_center',
            'title,description,body',
            HELP_CENTER_LIMIT,
        ),
    ];
};

const searchParamsResourceFactory = (
    query: string,
    resource: SearchCollectionName,
    originalResource: SearchCollectionName,
    queryBy: string,
    limit: number,
    filterBy?: string,
): SearchParam => ({
    limit,
    page: MULTI_SEARCH_PAGE,
    collection: resource,
    originalCollection: originalResource,
    q: query,
    query_by: queryBy,
    filter_by: filterBy,
});

const filterByResources = (collection: SearchCollectionName, resources: string[]) => {
    if (resources.length > 0) {
        return resources.includes(collection);
    }

    return true;
};

export { search };
export type {
    SearchCollectionName,
    SearchResponse,
    SearchResult,
    SearchDocument,
    SearchHit,
    SearchTextMatchInfo,
    SearchHighlight,
    SearchHighlightDetail,
    SearchHighlightWithField,
    SearchRequestParams,
    SearchParam,
};
